// ─── Server notification shape (from API) ──────────────────
export interface NotificationPayload {
  title: string;
  body: string;
  /**
   * Absent on aggregate notifications (e.g. automation_shuffle) — those are
   * deliberately entity-less so the server's scope filter never hides them
   * from a user who just LOST access to the entity.
   */
  entityType?: "lead" | "child";
  entityId?: number;
  /** Parent lead id when entityType is "child" — used to open View Lead. */
  leadId?: number;
  /** Parent lead display name (UI: lead = name; child = "(Child) leadName"). */
  leadName?: string;
  actorName: string;
  actorId: number;
  stageId?: number;
  fromValue?: string;
  toValue?: string;
  preview?: string;

  // ── automation_shuffle payload (kind: "shuffle") ──────────
  kind?: string;
  automationId?: number;
  automationName?: string;
  /** The automation's "Run only if…" condition, already humanized server-side. */
  conditionText?: string;
  lostCount?: number;
  gainedCount?: number;
  movedCount?: number;
  lost?: ShuffleLedgerItem[];
  gained?: ShuffleLedgerItem[];
  moved?: ShuffleMovedItem[];
  /** Branch-wide totals — present only for branch managers whose branches lost/gained leads. */
  branchDeltas?: ShuffleBranchDelta[];
  batchIds?: string[];
  runCount?: number;
  lastEventAt?: string;

  // ── automation_shuffle_warning payload (kind: "shuffle_warning") ──
  /** Approximate moment the shuffle will run (next sweep tick). */
  fireAtApprox?: string;
  /** Leads the user stands to lose unless they act before fireAtApprox. */
  leads?: Array<{ leadId: number; leadName: string | null }>;
  count?: number;

  // ── shared_view payload (an admin shared a list screen) ───
  /** Token of the share link — the card opens /shared/<token>. */
  shareToken?: string;
  /** What was shared ("Leads", the stage, the view name). */
  shareLabel?: string;
  shareKind?: "leads" | "stage" | "view";

  // ── hr payload (HR suite: leave / WFH / payslip / automation notices) ──
  // Entity-less like shared_view: rendered as `title` + `body`, and the row
  // opens `link` — an in-app path such as "/hr/attendance?tab=leave".
  link?: string;
  hrEntityType?:
    | "leave"
    | "wfh"
    | "payslip"
    | "payroll"
    | "employee"
    | "attendance"
    | "automation"
    | null;
  hrEntityId?: number | null;
}

export interface ShuffleBranchDelta {
  branchId: number;
  branchName: string;
  lost: number;
  gained: number;
}

export interface ShuffleLedgerItem {
  leadId: number | null;
  leadName: string | null;
  childId: number | null;
  childName: string | null;
  /** Who it went to (on lost items). */
  toUserId?: number | null;
  toUserName?: string | null;
  /** Who it came from (on gained items). */
  fromUserId?: number | null;
  fromUserName?: string | null;
  at: string;
}

export interface ShuffleMovedItem {
  leadId: number | null;
  leadName: string | null;
  childId: number | null;
  childName: string | null;
  fromBranchName: string | null;
  toBranchName: string | null;
  at: string;
}

export interface ServerNotification {
  id: number;
  userId: number;
  tenantId: number;
  type: NotificationType;
  payload: NotificationPayload;
  readAt: string | null;
  dismissedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

// ─── Notification types (no poke in v1) ────────────────────
export type NotificationType =
  | "stage_change"
  | "status_change"
  | "sub_status_change"
  | "note"
  | "chat"
  | "call_history"
  | "followup"
  | "attachment"
  | "automation_shuffle"
  | "automation_shuffle_warning"
  | "installment_due"
  | "shared_view"
  | "hr";

// ─── Filter categories (no poke in v1) ─────────────────────
export type FilterCategory =
  | "all"
  | "stage"
  | "status"
  | "sub_status"
  | "notes"
  | "chat"
  | "call_history"
  | "followup"
  | "attachments"
  | "installments";

export const FILTER_LABELS: Record<FilterCategory, string> = {
  all: "All Activity",
  stage: "Stage",
  status: "Status",
  sub_status: "Sub Status",
  notes: "Notes",
  chat: "Chat",
  call_history: "Call History",
  followup: "Followup",
  attachments: "Attachments",
  installments: "Instalments",
};

// ─── Filter type → notification type mapping ───────────────
export const FILTER_TO_TYPE_MAP: Record<string, NotificationType> = {
  stage: "stage_change",
  status: "status_change",
  sub_status: "sub_status_change",
  notes: "note",
  chat: "chat",
  call_history: "call_history",
  followup: "followup",
  attachments: "attachment",
  installments: "installment_due",
};

// Stage/Status/SubStatus options are now derived from tenant pipeline data
// via useStages() hook in NotificationFilters.tsx — no hardcoded arrays.
